import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Subject, Observable } from 'rxjs';

/**
 * AppLifecycleService
 *
 * Single authority for "the app just came back to the foreground after being
 * backgrounded long enough that our data may be stale."
 *
 * Why this exists:
 * - While the app is backgrounded/suspended, the chat WebSocket is torn down by
 *   the OS. `ReconnectingWebSocket` re-establishes on resume, but a WebSocket
 *   reconnect only resumes LIVE delivery — it does NOT replay messages that
 *   arrived while suspended. Those must be pulled via REST. So on foreground we
 *   have to proactively re-sync the thread list, unread counts, and the open
 *   thread's messages.
 * - We must be wise about it: a quick app-switch (glance at another app for a
 *   second) should NOT trigger a network sync. Only a meaningful background
 *   period should. Consumers also shouldn't each invent their own timing — this
 *   service is the one place that decides "did we background long enough to
 *   warrant a sync", so a resume + socket reconnect + route re-entry can't each
 *   fire their own refresh.
 *
 * Consumers subscribe to `resumed$` and decide their own scope:
 * - ChatService: refresh thread list + unread (app-wide, even if chat was never opened).
 * - ChatPage: backfill the currently-open thread's messages (only when one is open).
 */
@Injectable({
	providedIn: 'root',
})
export class AppLifecycleService {
	/** Minimum time backgrounded before a resume is considered sync-worthy. */
	public static readonly RESUME_SYNC_THRESHOLD_MS = 3000;

	private pausedAt: number | null = null;
	private _resumed = new Subject<number>();

	/** Emits the background duration (ms) when the app resumes after a sync-worthy pause. */
	public readonly resumed$: Observable<number> = this._resumed.asObservable();

	constructor() {
		if (Capacitor.isNativePlatform()) {
			// Canonical Capacitor signal on device.
			App.addListener('appStateChange', ({ isActive }) => this.handleActiveChange(isActive));
		} else if (typeof document !== 'undefined') {
			// Web/PWA fallback.
			document.addEventListener('visibilitychange', () => {
				this.handleActiveChange(document.visibilityState === 'visible');
			});
		}
	}

	private handleActiveChange(isActive: boolean): void {
		if (!isActive) {
			// Going to background — remember when.
			this.pausedAt = Date.now();
			return;
		}

		// Returning to foreground.
		const backgroundedFor = this.pausedAt === null ? 0 : Date.now() - this.pausedAt;
		this.pausedAt = null;

		if (backgroundedFor >= AppLifecycleService.RESUME_SYNC_THRESHOLD_MS) {
			console.log('AppLifecycleService - resumed after', backgroundedFor, 'ms backgrounded; emitting sync signal');
			this._resumed.next(backgroundedFor);
		} else {
			console.log('AppLifecycleService - resumed after only', backgroundedFor, 'ms; skipping sync');
		}
	}
}
